import { useMutation } from '@apollo/client';
import React from 'react';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import FormControl from 'react-bootstrap/FormControl';
import Image from 'react-bootstrap/Image';
import Row from 'react-bootstrap/Row';
import { ContentCategoriesQuery } from '../../../../../../__gqltypes__';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import { UPLOAD_ASSET } from '../../../components/apolloQueries';
import SelectDropdown from '../../../components/SelectDropdown';
import { AVAILABILITY_TYPES, CURRENCY_TYPES, getVanityPieceType } from '../utils';
import AssetDisplay from './AssetDisplay';
import DragUpload from './DragUpload';
import VanityPiecePicker from './VanityPiecePicker';
import { Item, OnValidateItem, SetItemData } from './typedef';

type Props = {
  itemData: Item;
  setItemData: SetItemData;
  refetch: () => void;
  categories: ContentCategoriesQuery['app']['contentCategories'];
  type: string;
  onBeforeRefetch?: () => void;
  onValidate?: OnValidateItem;
};

export default function ItemForm({
  itemData,
  setItemData,
  refetch,
  categories,
  type,
  onBeforeRefetch,
  onValidate,
}: Props) {
  const [uploadAsset, { loading: uploadLoading }] = useMutation(UPLOAD_ASSET, {
    onCompleted: (d) => {
      const asset = d?.uploadAsset?.item?.asset;

      if (type === 'Frame' && asset) {
        // @ts-ignore - functional update avoids wiping unsaved local edits like name
        setItemData((prev) => ({ ...prev, asset }));
        onBeforeRefetch?.();
      }

      refetch();
    },
  });

  const handleUpload = (file: Blob) => {
    uploadAsset({
      variables: {
        input: {
          file,
          id: itemData.id,
        },
      },
    });
  };

  // @ts-ignore
  const onAcquisitionSelect = (e) => {
    // @ts-ignore
    setItemData({
      ...itemData,
      liveOpsAvailability: e.target.value,
      cost:
        // @ts-ignore
        AVAILABILITY_TYPES[e.target.value].length > 0
          ? e.target.value === 'UNAVAILABLE'
            ? // @ts-ignore
              itemData.cost || { __typename: 'CurrencyCategoryFragment', cost: 0 }
            : // @ts-ignore
              { ...itemData.cost, __typename: CURRENCY_TYPES[AVAILABILITY_TYPES[e.target.value][0]] }
          : null,
    });
  };

  const options: Record<string, { value: React.ReactNode; searchValue: string }> = {};
  categories.forEach((category) => {
    options[category.id] = {
      value: (
        <>
          <Image roundedCircle className="img-profile mr-3" src={category.icon} style={{ width: 20, height: 20 }} />
          {category.name}
        </>
      ),
      searchValue: category.name,
    };
  });

  return (
    <Form>
      <Form.Group>
        <Form.Label>Id</Form.Label>
        <Form.Control placeholder={itemData.id} disabled />
      </Form.Group>
      <Form.Group>
        <Form.Label>Name</Form.Label>
        <Form.Control
          placeholder=""
          value={itemData.name}
          onChange={(e) => setItemData({ ...itemData, name: e.target.value })}
        />
      </Form.Group>
      <Form.Group>
        <Form.Label>Acquisition</Form.Label>
        <Form.Control as="select" value={itemData.liveOpsAvailability} onChange={onAcquisitionSelect}>
          {Object.keys(AVAILABILITY_TYPES).map((availabilityType) => (
            <option value={availabilityType} key={availabilityType}>
              {availabilityType}
            </option>
          ))}
        </Form.Control>
      </Form.Group>
      <hr />
      <h5> Item level1 (base item) </h5>
      <DragUpload onUpload={handleUpload} onValidate={onValidate} />
      <LoadingLogo show={uploadLoading} />
      <Form.Group>
        <Form.Label>Asset</Form.Label>
        <Form.Control value={itemData.asset} disabled />
      </Form.Group>
      <AssetDisplay assetUrl={itemData.asset} />
      {itemData.cost && (
        <div>
          <p>Cost</p>
          <Row>
            <Col>
              {/* @ts-ignore */}
              {AVAILABILITY_TYPES[itemData.liveOpsAvailability].length > 1 ? (
                <Form.Group>
                  <FormControl
                    as="select"
                    value={itemData.cost.__typename}
                    onChange={(e) =>
                      // @ts-ignore
                      setItemData({ ...itemData, cost: { ...itemData.cost, __typename: e.target.value } })
                    }
                  >
                    {/* @ts-ignore */}
                    {AVAILABILITY_TYPES[itemData.liveOpsAvailability].map((key) => (
                      // @ts-ignore
                      <option value={CURRENCY_TYPES[key]} key={key}>
                        {/* @ts-ignore */}
                        {CURRENCY_TYPES[key]}
                      </option>
                    ))}
                  </FormControl>
                </Form.Group>
              ) : (
                // @ts-ignore
                <p>{CURRENCY_TYPES[AVAILABILITY_TYPES[itemData.liveOpsAvailability][0]]}: </p>
              )}
            </Col>
            {itemData.cost.__typename === CURRENCY_TYPES.CURRENCY_CATEGORY_FRAGMENT && (
              <Col>
                <SelectDropdown
                  title="Select fragment currency"
                  options={options}
                  // @ts-ignore
                  value={itemData.cost?.category ? itemData.cost.category.id : null}
                  onSelect={(v) =>
                    setItemData({
                      ...itemData,
                      // @ts-ignore
                      cost: { ...itemData.cost, category: { id: v } },
                    })
                  }
                />
              </Col>
            )}
            {itemData.cost.__typename === CURRENCY_TYPES.CURRENCY_VANITY_PIECE && (
              <Col>
                <VanityPiecePicker
                  type={getVanityPieceType(type)}
                  // @ts-ignore
                  value={itemData.cost?.vanityPiece ? itemData.cost.vanityPiece.id : null}
                  onSelect={(v) =>
                    setItemData({
                      ...itemData,
                      // @ts-ignore
                      cost: { ...itemData.cost, vanityPiece: { id: v } },
                    })
                  }
                />
              </Col>
            )}
            <Col>
              <Form.Group>
                <FormControl
                  // @ts-ignore
                  value={itemData.cost?.amount ?? undefined}
                  // @ts-ignore
                  onChange={(e) => setItemData({ ...itemData, cost: { ...itemData.cost, amount: e.target.value } })}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}
    </Form>
  );
}
